import {
  AlertCircle,
  Compass,
  ListChecks,
  ShieldCheck,
  Sparkles,
  XCircle,
} from 'lucide-react';
import { NORTHWIND_PHILOSOPHY } from '@/modules/brain/data/seedCompany';
import { companyApi } from '@/modules/brain/services/company.api';
import { useCompanyResource } from '@/modules/brain/hooks/useCompanyResource';

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

export function HiringPhilosophyPage() {
  const { value: p } = useCompanyResource(
    () => companyApi.getPhilosophy(),
    NORTHWIND_PHILOSOPHY,
    'philosophy',
  );

  return (
    <div className="brain-company-page">
      <header className="brain-company-hero brain-company-hero-compact">
        <div className="brain-company-hero-text">
          <p className="brain-company-eyebrow">Brain · Company</p>
          <h1 className="brain-company-title">Hiring philosophy</h1>
          <p className="brain-company-tagline">
            How we calibrate, what we will not compromise on, and the shape of every loop.
          </p>
        </div>
      </header>

      <section className="brain-company-section">
        <header className="brain-company-section-header">
          <Sparkles size={14} strokeWidth={1.6} />
          <h2>The bar</h2>
        </header>
        <p className="brain-company-prose brain-company-prose-pull">{p.bar}</p>
      </section>

      <div className="brain-company-grid">
        <section className="brain-company-card">
          <header className="brain-company-card-header">
            <ShieldCheck size={14} strokeWidth={1.6} />
            <h3>Non-negotiables</h3>
            <span className="brain-company-card-count">{p.nonNegotiables.length}</span>
          </header>
          <ul className="brain-company-bullet-list">
            {p.nonNegotiables.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </section>

        <section className="brain-company-card">
          <header className="brain-company-card-header">
            <XCircle size={14} strokeWidth={1.6} />
            <h3>Dealbreakers</h3>
            <span className="brain-company-card-count">{p.dealbreakers.length}</span>
          </header>
          <ul className="brain-company-dealbreaker-list">
            {p.dealbreakers.map((d, i) => (
              <li key={i}>
                <div className="brain-company-dealbreaker-signal">
                  <AlertCircle size={11} strokeWidth={1.8} />
                  <span>{d.signal}</span>
                </div>
                <p className="brain-company-dealbreaker-reason">{d.reason}</p>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <section className="brain-company-section">
        <header className="brain-company-section-header">
          <Compass size={14} strokeWidth={1.6} />
          <h2>Calibrations</h2>
          <span className="brain-company-section-hint">
            Patterns Lantern has learned from your team's score edits.
          </span>
        </header>
        <ul className="brain-company-calibration-list">
          {p.calibrations.map((c, i) => (
            <li key={i} className="brain-company-calibration">
              <div className="brain-company-calibration-rule">{c.rule}</div>
              <p className="brain-company-calibration-rationale">{c.rationale}</p>
              <div className="brain-company-calibration-meta">
                <span>From <strong>{c.addedBy}</strong></span>
                <span aria-hidden="true">·</span>
                <span>{formatDate(c.addedAt)}</span>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="brain-company-section">
        <header className="brain-company-section-header">
          <ListChecks size={14} strokeWidth={1.6} />
          <h2>Interview loop principles</h2>
        </header>
        <ul className="brain-company-bullet-list brain-company-bullet-list-spaced">
          {p.interviewLoopPrinciples.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      </section>
    </div>
  );
}
